import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';

export interface QuickActionItem {
  id: string;
  icon?: string;
  label: string;
  prompt: string;
}

export interface SelectedElementNode {
  id?: string;
  nodeName: string;
  kind?: 'self' | 'image' | 'link';
  className?: string;
  src?: string;
  href?: string;
}

export interface SelectedElementValueTarget {
  id?: string;
  nodeName: string;
  value: string;
}

export interface SelectedElementInfo {
  id: string;
  text: string;
  textList?: string[];
  textTargetId?: string;
  textTargets?: SelectedElementValueTarget[];
  nodeName: string;
  bindingTargetId?: string;
  bindingTargetSelector?: string;
  bindingTargetNodeName?: string;
  bindingTargetText?: string;
  src?: string;
  href?: string;
  srcTargetId?: string;
  hrefTargetId?: string;
  hrefTargets?: SelectedElementValueTarget[];
  selectionNodes?: SelectedElementNode[];
}

export interface LinkerHost {
  send: (type: string, data: unknown) => void;
  on: (type: string, cb: (data: unknown) => void) => void;
}
export interface CreatorState {
  projectId: string | null;
  isCreating: boolean;
  isEditing: boolean;
  currentTaskText: string;
  tasks: { id: string; text: string; status: 'processing' | 'done'; at: string }[];
  wordsItems: QuickActionItem[];
  lookItems: QuickActionItem[];
  partsItems: QuickActionItem[];
  impeccableItems: QuickActionItem[];
  infoItems: QuickActionItem[];
  promptInput: string;
  selectedElement: SelectedElementInfo | null;
  linkerHost: LinkerHost | null;

  // --- Actions ---
  setProjectId: (id: string | null) => void;
  setPromptInput: (text: string) => void;
  setSelectedElement: (el: SelectedElementInfo | null) => void;
  setLinkerHost: (host: LinkerHost | null) => void;
  setCreating: (val: boolean, text?: string) => void;
  setEditing: (val: boolean) => void;
  addTask: (text: string) => void;
  updateTaskStatus: (id: string, status: 'done') => void;
  removeTask: (id: string) => void;
  setWordsItems: (items: QuickActionItem[]) => void;
  setLookItems: (items: QuickActionItem[]) => void;
  setPartsItems: (items: QuickActionItem[]) => void;
  setImpeccableItems: (items: QuickActionItem[]) => void;
  setInfoItems: (items: QuickActionItem[]) => void;
  resetCreatorState: () => void;
}
export const useCreatorStore = create<CreatorState>()(
  persist(
    (set) => ({
      projectId: null,
      isCreating: false,
      isEditing: false,
      currentTaskText: '',
      tasks: [],
      promptInput: '',
      selectedElement: null,
      linkerHost: null,

      wordsItems: [],
      lookItems: [],
      partsItems: [],
      impeccableItems: [],
      infoItems: [],

      setProjectId: (projectId) => set({ projectId }),
      setPromptInput: (promptInput) => set({ promptInput }),
      setSelectedElement: (selectedElement) => set({ selectedElement }),
      setLinkerHost: (linkerHost) => set({ linkerHost }),
      setCreating: (isCreating, text = '') =>
        set((state) => ({
          isCreating,
          currentTaskText: text || (isCreating ? state.currentTaskText : ''),
        })),
      setEditing: (isEditing) => set({ isEditing }),
      addTask: (text) => {
        const newTask = {
          id: `task_${nanoid()}`,
          text,
          status: 'processing' as const,
          at: new Date().toISOString(),
        };
        set((state) => ({ tasks: [newTask, ...state.tasks] }));
      },
      updateTaskStatus: (id, status) =>
        set((state) => ({
          tasks: state.tasks.map((t) => (t.id === id ? { ...t, status } : t)),
        })),
      removeTask: (id) =>
        set((state) => ({
          tasks: state.tasks.filter((t) => t.id !== id),
        })),
      setWordsItems: (wordsItems) => set({ wordsItems }),
      setLookItems: (lookItems) => set({ lookItems }),
      setPartsItems: (partsItems) => set({ partsItems }),
      setImpeccableItems: (impeccableItems) => set({ impeccableItems }),
      setInfoItems: (infoItems) => set({ infoItems }),
      resetCreatorState: () =>
        set({
          projectId: null,
          isCreating: false,
          isEditing: false,
          tasks: [],
          promptInput: '',
          selectedElement: null,
          linkerHost: null,
          wordsItems: [],
          lookItems: [],
          partsItems: [],
          impeccableItems: [],
          infoItems: [],
        }),
    }),
    {
      name: 'creator-review-storage',
      partialize: (state) => ({
        projectId: state.projectId,
        wordsItems: state.wordsItems,
        lookItems: state.lookItems,
        partsItems: state.partsItems,
        impeccableItems: state.impeccableItems,
        infoItems: state.infoItems,
      }),
    },
  ),
);
